import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {usePopQuizAttemptLookupQuery, usePopQuizCompletedQuizzesQuery} from 'src/generated/graphql';
import {PopQuizSetting} from 'src/lib/pop-quiz/pop-quiz-settings';

import {QuizCandidate} from '../types';
import {isCapped} from '../utils';
import {selectAttemptLookupQuizIds} from './attempt-lookup-ids';
import {
    buildDefaultCandidates,
    buildRefresherCandidates,
    buildRequizCandidates,
    CandidateAttemptInfo,
    CandidateStrategyContext,
} from './candidate-strategies';
import {MAX_ATTEMPT_LOOKUPS} from './constants';
import {selectNextCandidate} from './ranking';

interface UseQuizOrchestratorArgs {
    enablePopQuizPP: boolean;
    popQuizV2ExperienceEnabled: boolean;
    getPopQuizSetting: (setting: PopQuizSetting) => string;
    forcePopQuiz: boolean;
}

interface UseQuizOrchestratorResult {
    quizData: QuizCandidate | null;
    isLoading: boolean;
    hasAnotherCandidate: boolean;
    requestDifferentQuiz: () => void;
}

/**
 * Owns "which quiz (and why) should we show right now." Gathers candidates from all
 * registered strategies into a pool, then ranks and selects one.
 */
export function useQuizOrchestrator({
    enablePopQuizPP,
    popQuizV2ExperienceEnabled,
    getPopQuizSetting,
    forcePopQuiz,
}: UseQuizOrchestratorArgs): UseQuizOrchestratorResult {
    const capped = forcePopQuiz ? false : isCapped(getPopQuizSetting);
    const enrollmentsQueryEnabled = enablePopQuizPP && !capped;

    const {data: enrollmentsData, isLoading} = usePopQuizCompletedQuizzesQuery(
        {},
        {enabled: enrollmentsQueryEnabled},
    );

    // Requiz/refresher need each candidate quiz's latest attempt. Cap the lookup fan-out at
    // MAX_ATTEMPT_LOOKUPS, spread round-robin across enrollments
    const candidateQuizIds = useMemo(() => {
        if (!popQuizV2ExperienceEnabled) {
            return [];
        }
        const enrollments =
            enrollmentsData?.me?.enrollments?.courseEnrollmentsPaged?.courseEnrollments;
        return selectAttemptLookupQuizIds(enrollments, MAX_ATTEMPT_LOOKUPS);
    }, [enrollmentsData, popQuizV2ExperienceEnabled]);

    const paddedQuizIds = useMemo(() => {
        if (candidateQuizIds.length === 0) {
            return null;
        }
        const padded = [...candidateQuizIds];
        // '' is a no-op lookup (same sentinel used below when there are no candidates at all) —
        // padding with a real quiz ID here would just cause redundant, wasted lookups for it.
        while (padded.length < MAX_ATTEMPT_LOOKUPS) {
            padded.push('');
        }
        return padded;
    }, [candidateQuizIds]);

    const attemptLookupVariables = {
        quizId0: paddedQuizIds?.[0] ?? '',
        quizId1: paddedQuizIds?.[1] ?? '',
        quizId2: paddedQuizIds?.[2] ?? '',
        quizId3: paddedQuizIds?.[3] ?? '',
        quizId4: paddedQuizIds?.[4] ?? '',
        quizId5: paddedQuizIds?.[5] ?? '',
        quizId6: paddedQuizIds?.[6] ?? '',
        quizId7: paddedQuizIds?.[7] ?? '',
        quizId8: paddedQuizIds?.[8] ?? '',
        quizId9: paddedQuizIds?.[9] ?? '',
    };

    const {data: attemptLookupData, isLoading: isAttemptLookupLoading} =
        usePopQuizAttemptLookupQuery(attemptLookupVariables, {
            enabled: enrollmentsQueryEnabled && !!paddedQuizIds,
        });

    const attemptsById = useMemo(() => {
        if (!attemptLookupData || !paddedQuizIds) {
            return undefined;
        }
        const attemptsInOrder = [
            attemptLookupData.q0,
            attemptLookupData.q1,
            attemptLookupData.q2,
            attemptLookupData.q3,
            attemptLookupData.q4,
            attemptLookupData.q5,
            attemptLookupData.q6,
            attemptLookupData.q7,
            attemptLookupData.q8,
            attemptLookupData.q9,
        ];
        const map: Record<string, CandidateAttemptInfo> = {};
        paddedQuizIds.forEach((quizId, index) => {
            const attempt = attemptsInOrder[index];
            if (attempt && attempt.completionTime && !map[quizId]) {
                map[quizId] = {
                    hasMetPassingScore: attempt.hasMetPassingScore,
                    completionTime: attempt.completionTime,
                };
            }
        });
        return map;
    }, [attemptLookupData, paddedQuizIds]);

    const pool = useMemo(() => {
        const context: CandidateStrategyContext = {
            enrollmentsData,
            getPopQuizSetting,
            force: forcePopQuiz,
            attemptsById,
        };
        if (!popQuizV2ExperienceEnabled) {
            return buildDefaultCandidates(context);
        }
        // Order doesn't matter here — rankCandidates re-groups by tier regardless.
        return [
            ...buildRequizCandidates(context),
            ...buildRefresherCandidates(context),
            ...buildDefaultCandidates(context),
        ];
    }, [
        enrollmentsData,
        getPopQuizSetting,
        forcePopQuiz,
        attemptsById,
        popQuizV2ExperienceEnabled,
    ]);

    const [excludedIds, setExcludedIds] = useState<ReadonlySet<number>>(() => new Set());
    const [quizData, setQuizData] = useState<QuizCandidate | null>(null);
    const hasProcessedRef = useRef(false);
    const lastExcludedIdsRef = useRef(excludedIds);

    // The attempt-lookup query is causally downstream of the enrollments query (it only
    // enables once enrollments resolve), so it can't have data yet on the render where
    // `isLoading` first flips false. Without waiting for it too, the initial pick locks in
    // off a pool that only has 'default' candidates, and requiz/refresher never get a shot —
    // re-selection only happens again on an explicit "show me another" (see below). This is
    // "ready" once there's nothing left to wait for: V2 is off, there were no candidate quiz
    // IDs to look attempts up for, or the attempt-lookup query is no longer loading (settled,
    // success or error — react-query flips isLoading false either way).
    const attemptDataReady =
        !popQuizV2ExperienceEnabled || !paddedQuizIds || !isAttemptLookupLoading;

    useEffect(() => {
        if (isLoading || capped || !attemptDataReady) {
            return;
        }
        const excludedIdsChanged = lastExcludedIdsRef.current !== excludedIds;
        if (!hasProcessedRef.current) {
            hasProcessedRef.current = true;
            lastExcludedIdsRef.current = excludedIds;
            setQuizData(selectNextCandidate(pool, excludedIds));
            return;
        }
        // Re-select only in response to an explicit "Show me another" - TODO in SLTV-540
        if (excludedIdsChanged) {
            lastExcludedIdsRef.current = excludedIds;
            setQuizData(selectNextCandidate(pool, excludedIds));
        }
    }, [pool, isLoading, capped, excludedIds, attemptDataReady]);

    const requestDifferentQuiz = useCallback(() => {
        if (!quizData) {
            return;
        }
        setExcludedIds((prev) => new Set(prev).add(quizData.quizId));
    }, [quizData]);

    const hasAnotherCandidate = useMemo(() => {
        if (!quizData) {
            return false;
        }
        const nextExcludedIds = new Set(excludedIds);
        nextExcludedIds.add(quizData.quizId);
        return selectNextCandidate(pool, nextExcludedIds) !== null;
    }, [pool, excludedIds, quizData]);

    return {quizData, isLoading, hasAnotherCandidate, requestDifferentQuiz};
}
